import { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { FilterModel, IServerSideGetRowsParams } from 'ag-grid-community';
import _, { cloneDeep } from 'lodash';

import { productApiClient } from '../clients/ProductAPIClient';
import { masterAPIClient } from '../clients/MasterAPIClient';
import { IProductList } from '../data/models/productList';
import { IFetchParameters } from '../data/models/fetchParameter';
import { AlbumState } from '../data/models/albumState';
import {
  Delete,
  Duplicate,
  DuplicateForDigital,
  Edit,
} from '../constants/Operations';
import { getAuth } from '../selectors/auth';
import {
  Filters,
  SearchFields,
} from '../components/ProductSearch/ProductSearch';
import { logger } from '../utils/Logger';
import { ChecklistFilterOption } from '../components/common/Grid/ChecklistFilter/ChecklistFilter';
import { TranslatableColDef } from '../components/common/Grid/TranslatableColDef';
import { Format } from '@/data/models/format';
import useProductStateTransition from './useProductStateTransition';
import { FormatType } from '@/data/models/formatType';
import { getVideoContentTypeDisplayName } from '@/utils/helper';
import { getPrimaryPpdReferencePriceOrLegacyPriceCode } from '@/utils/pricingUtils';
import useAuthUtilities from '@/AuthMiddleware/AuthUtilities';
import {
  getProductViewMode,
  ProductViewModes,
} from '@/utils/productStateUtils';

const defaultFilters: Filters = {
  label: '',
  catalogNumber: '',
  includeSublabels: false,
  releaseDate: '',
};

const defaultSearchFields: SearchFields = {
  keyword: '',
  artist: '',
  title: '',
};

const albumStateFilters: ChecklistFilterOption[] = [
  { searchKey: AlbumState.Draft.toString(), translationKey: 'product.Draft' },
  {
    searchKey: AlbumState.UnderReview.toString(),
    translationKey: 'product.UnderReview',
  },
  {
    searchKey: AlbumState.PendingImport.toString(),
    translationKey: 'product.PendingImport',
  },
  {
    searchKey: AlbumState.Rejected.toString(),
    translationKey: 'product.Rejected',
  },
  {
    searchKey: AlbumState.Released.toString(),
    translationKey: 'product.Released',
  },
  {
    searchKey: AlbumState.Error.toString(),
    translationKey: 'product.Error',
  },
];

const defaultFormatFilter: ChecklistFilterOption[] = [
  {
    searchKey: Format.CD.toString(),
    translationKey: 'product.format.cd',
  },
  {
    searchKey: Format.Vinyl12Inch.toString(),
    translationKey: 'product.format.vinyl12Inch',
  },
  {
    searchKey: Format.Vinyl10Inch.toString(),
    translationKey: 'product.format.vinyl10Inch',
  },
  {
    searchKey: Format.Vinyl7Inch.toString(),
    translationKey: 'product.format.vinyl7Inch',
  },
  {
    searchKey: Format.Cassette.toString(),
    translationKey: 'product.format.cassette',
  },
  {
    searchKey: Format.SACD.toString(),
    translationKey: 'product.format.sacd',
  },
  {
    searchKey: Format.DVD.toString(),
    translationKey: 'product.format.dvd',
  },
  {
    searchKey: Format.BluRay.toString(),
    translationKey: 'product.format.bluray',
  },
];

const merchandiseFormatFilter: ChecklistFilterOption[] = [
  {
    searchKey: Format.Apparel.toString(),
    translationKey: 'product.format.apparel',
  },
  {
    searchKey: Format.Print.toString(),
    translationKey: 'product.format.print',
  },
  {
    searchKey: Format.Accessories.toString(),
    translationKey: 'product.format.accessories',
  },
  {
    searchKey: Format.HardGoods.toString(),
    translationKey: 'product.format.hardGoods',
  },
];

export const useProductList = (
  defaultStateFilters: AlbumState[],
  storedGridParams: URLSearchParams,
  setGridParams: any,
  productListColDef: TranslatableColDef[],
  formatType: FormatType,
  preferPrimaryPpdReferencePrice = false
) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { hasWriteAccess } = useSelector(getAuth);
  const { canUserEditProduct } = useAuthUtilities();
  const { productState, clearProductState } =
    useProductStateTransition(formatType);

  const [refreshGrid, setRefreshGrid] = useState<boolean>(false);
  const [warningHeader, setWarningHeader] = useState<string>('');
  const [selectedId, setselectedId] = useState<number>(0);
  const [resultCount, setResultCount] = useState(0);
  const [fetchProductParams, setFetchProductParams] = useState<
    IFetchParameters & { formatType: FormatType }
  >();
  const [showPopUp, setShowPopUp] = useState(false);
  const [showCreateProductModal, setShowCreateProductModal] = useState(false);
  const [showDuplicateForDigitalModal, setShowDuplicateForDigitalModal] =
    useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [albumToCopy, setAlbumToCopy] = useState<IProductList>();
  const [externalFiltersUpdated, setExternalFiltersUpdated] = useState(false);

  const [searchParams, setSearchParams] = useSearchParams(
    location.search ? undefined : storedGridParams
  );

  const initialFilters: Filters = {
    label: searchParams.get('label') ?? defaultFilters.label,
    catalogNumber: searchParams.get('upc') ?? defaultFilters.catalogNumber,
    includeSublabels: searchParams.get('includeSublabels')
      ? searchParams.get('includeSublabels')!.toLowerCase() === 'true'
      : defaultFilters.includeSublabels,
    releaseDate: searchParams.get('releaseDate') ?? defaultFilters.releaseDate,
  };

  const initialSearch: SearchFields = {
    keyword: searchParams.get('keyword') ?? defaultSearchFields.keyword,
    artist: searchParams.get('artist') ?? defaultSearchFields.artist,
    title: searchParams.get('title') ?? defaultSearchFields.title,
  };

  const currentFormatFilterOptions =
    formatType === FormatType.Merchandise
      ? merchandiseFormatFilter
      : defaultFormatFilter;

  const initialAlbumStateFilter: string[] =
    searchParams
      .get('status')
      ?.split(',')
      .filter((s) => s.trim() !== '') ??
    defaultStateFilters?.map((dsf) => dsf.toString()) ??
    albumStateFilters.map((asf) => asf.searchKey);

  const initialFormatFilter: string[] =
    searchParams
      .get('format')
      ?.split(',')
      .filter((s) => s.trim() !== '') ??
    currentFormatFilterOptions.map((dcf) => dcf.searchKey);

  const initialMerchandiseTypeFilter: string[] =
    searchParams
      .get('subType')
      ?.split(',')
      .filter((s) => s.trim() !== '') ?? [];

  const [filterOptions, setFilterOptions] = useState(initialFilters);
  const [searchOptions, setSearchOptions] = useState(initialSearch);
  const [albumStateFilter, setAlbumStateFilter] = useState(
    initialAlbumStateFilter
  );
  const [formatFilter, setFormatFilter] = useState(initialFormatFilter);
  const [merchandiseTypeFilter, setMerchandiseTypeFilter] = useState(
    initialMerchandiseTypeFilter
  );
  const [merchandiseTypeFilters, setMerchandiseTypeFilters] = useState<
    ChecklistFilterOption[]
  >([]);

  useEffect(() => {
    setSearchParams(searchParams);
  }, []);

  useEffect(() => {
    dispatch(setGridParams(searchParams));
  }, [searchParams]);

  useEffect(() => {
    if (location.state) {
      const timeoutId = setTimeout(clearProductState, 5000);
      return () => clearTimeout(timeoutId);
    }
  }, [location, navigate]);

  useEffect(() => {
    if (formatType === FormatType.Merchandise) {
      masterAPIClient
        .getMerchandiseTypes()
        .then((data) => {
          const options = data.map((d: any) => ({
            searchKey: d.value?.name ?? d.label,
            translationKey: `product.merchandiseType.${_.camelCase(d.label)}`,
          }));
          setMerchandiseTypeFilters(options);
        })
        .catch((error) => logger.error(error));
    }
  }, [formatType]);

  const updateFilters = useCallback(
    (filters: Filters, search: SearchFields) => {
      setFilterOptions(filters);
      setSearchOptions(search);
      setSearchParams((prev) => {
        Object.keys(filterOptions).forEach((key) => {
          if (filters[key]) prev.set(key, filters[key]);
          else prev.delete(key);
        });
        Object.keys(searchOptions).forEach((key) => {
          if (search[key]) prev.set(key, search[key]);
          else prev.delete(key);
        });
        return prev;
      });
      setExternalFiltersUpdated(true);
    },
    [setSearchParams]
  );

  const updateAlbumStateFilter = useCallback(
    (albumStateFilter: string[]) => {
      setAlbumStateFilter(albumStateFilter);
      setSearchParams((prev) => {
        prev.set('status', albumStateFilter.join(','));
        return prev;
      });
      setExternalFiltersUpdated(true);
    },
    [setSearchParams]
  );

  const updateFormatFilter = useCallback(
    (formatFilter: string[]) => {
      setFormatFilter(formatFilter);
      setSearchParams((prev) => {
        prev.set('format', formatFilter.join(','));
        return prev;
      });
      setExternalFiltersUpdated(true);
    },
    [setSearchParams]
  );

  const updateMerchandiseTypeFilter = useCallback(
    (merchandiseTypeFilter: string[]) => {
      setMerchandiseTypeFilter(merchandiseTypeFilter);
      setSearchParams((prev) => {
        if (merchandiseTypeFilter.length > 0) {
          prev.set('subType', merchandiseTypeFilter.join(','));
        } else {
          prev.delete('subType');
        }
        return prev;
      });
      setExternalFiltersUpdated(true);
    },
    [setSearchParams]
  );

  const resetColumnFilters = useCallback(() => {
    setAlbumStateFilter(defaultStateFilters.map((state) => state.toString()));
    setFormatFilter(
      currentFormatFilterOptions.map((option) => option.searchKey)
    );
    setMerchandiseTypeFilter([]);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('status');
      next.delete('format');
      next.delete('subType');
      return next;
    });
    setExternalFiltersUpdated(true);
  }, [currentFormatFilterOptions, defaultStateFilters, setSearchParams]);

  const onSortUpdated = useCallback(
    (columnState: any) => {
      const updatedSort = convertColumnStateToSortParams(columnState);
      setSearchParams((prev) => {
        if (updatedSort?.length > 0) prev.set('sort', updatedSort);
        else
          prev.set('sort', convertColumnStateToSortParams(productListColDef));
        return prev;
      });
    },
    [setSearchParams, productListColDef]
  );

  const convertColumnStateToSortParams = (columnState: any) => {
    const sort = columnState
      .filter((s: any[]) => !!s.sort)
      .sort((a: any, b: any) => a.sortIndex - b.sortIndex)
      .map(
        (x: { colId: string; sort: string; field: string }) =>
          `${getSortParam(x.colId ?? x.field, x.sort)}`
      );
    return sort;
  };

  const getSortParam = (colId: string, sortDirection: string) => {
    switch (sortDirection) {
      case 'asc':
        return `${colId}`;
      case 'desc':
        return `-${colId}`;
      default:
        return `${colId}`;
    }
  };

  const finalColumndefs = useMemo(() => {
    const columnDefs: TranslatableColDef[] = [];
    const sortParams =
      searchParams
        .get('sort')
        ?.split(',')
        .map((sp) => sp.toLowerCase()) ?? [];
    productListColDef.forEach((plc) => {
      const columnName = plc?.field?.toLowerCase() ?? '';
      const columnDef = cloneDeep(plc);
      if (sortParams.includes(columnName)) {
        columnDef.sort = 'asc';
      } else if (sortParams.includes(`-${columnName}`)) {
        columnDef.sort = 'desc';
      } else if (sortParams.length) {
        columnDef.sort = null;
      }

      columnDef.headerName = plc?.headerNameTranslationKey
        ? t(plc?.headerNameTranslationKey)
        : plc?.headerName;
      if (plc?.field?.toLowerCase() === 'albumstate') {
        columnDef.filterParams = {
          options: albumStateFilters,
          initialList: initialAlbumStateFilter,
          updateFilter: updateAlbumStateFilter,
        };
      }
      if (plc?.field?.toLowerCase() === 'format') {
        columnDef.filterParams = {
          options: currentFormatFilterOptions,
          initialList: initialFormatFilter,
          updateFilter: updateFormatFilter,
        };
      }
      if (plc?.field?.toLowerCase() === 'merchandisemetadata.merchandisetype') {
        columnDef.filterParams = {
          options: merchandiseTypeFilters,
          initialList: initialMerchandiseTypeFilter.length
            ? initialMerchandiseTypeFilter
            : undefined,
          updateFilter: updateMerchandiseTypeFilter,
        };
      }
      if (plc?.field?.toLowerCase() === 'videocontenttype') {
        columnDef.valueFormatter = ({ value }) =>
          getVideoContentTypeDisplayName(value, t);
        columnDef.tooltipValueGetter = ({ value }) =>
          getVideoContentTypeDisplayName(value, t);
        columnDef.tooltipField = undefined;
      }
      delete columnDef.headerNameTranslationKey;
      columnDefs.push(columnDef);
    });
    return columnDefs;
  }, [
    searchParams,
    t,
    productListColDef,
    currentFormatFilterOptions,
    merchandiseTypeFilters,
    updateAlbumStateFilter,
    updateFormatFilter,
    updateMerchandiseTypeFilter,
  ]);

  const getRows = useCallback(
    (params: IServerSideGetRowsParams) => {
      const {
        startRow,
        endRow,
        sortModel,
        filterModel: agGridFilterModel,
      } = params.request;
      const filterModel = agGridFilterModel as FilterModel;
      if (searchOptions.keyword) {
        filterModel['keyword'] = {
          filterType: 'text',
          type: 'contains',
          filter: searchOptions.keyword,
        };
      }
      if (searchOptions.title) {
        filterModel['title'] = {
          filterType: 'text',
          type: 'contains',
          filter: searchOptions.title,
        };
      }
      if (searchOptions.artist) {
        filterModel['displayArtist'] = {
          filterType: 'text',
          type: 'contains',
          filter: searchOptions.artist,
        };
      }
      if (filterOptions.catalogNumber) {
        filterModel['catalogNumber'] = {
          filterType: 'text',
          type: 'equals',
          filter: filterOptions.catalogNumber,
        };
      }
      if (filterOptions.label) {
        const labelIds = filterOptions.label.split(',').filter(Boolean);
        if (labelIds.length === 1) {
          filterModel['label.adminOrgId'] = {
            filterType: 'guid',
            type: 'equals',
            filter: labelIds[0],
          };
        } else if (labelIds.length > 1) {
          filterModel['label.adminOrgId'] = {
            filterType: 'guid',
            operator: 'OR',
            conditions: labelIds.map((labelId) => ({
              filterType: 'guid',
              type: 'equals',
              filter: labelId,
            })),
          };
        }
      }
      if (albumStateFilter) {
        filterModel['albumState'] = {
          filterType: 'set',
          values: albumStateFilter,
        };
      }
      if (formatFilter) {
        filterModel['format'] = {
          filterType: 'set',
          values: formatFilter,
        };
      }
      if (merchandiseTypeFilter && merchandiseTypeFilter.length > 0) {
        filterModel['merchandiseMetadata.merchandiseType'] = {
          filterType: 'set',
          values: merchandiseTypeFilter,
        };
      }
      const activeChangeRequestFilter = (filterModel as any)[
        'activeChangeRequestId'
      ];
      if (
        activeChangeRequestFilter?.filterType === 'set' ||
        activeChangeRequestFilter?.filterType === 'checklist'
      ) {
        const values = Array.isArray(activeChangeRequestFilter.values)
          ? activeChangeRequestFilter.values
          : [];
        filterModel['activeChangeRequestId'] = {
          filterType: 'set',
          values,
        } as any;
      }
      const fetchParam: IFetchParameters & { formatType: FormatType } = {
        startRow: startRow,
        endRow: endRow,
        sortModel: sortModel,
        filterModel: filterModel,
        formatType: formatType,
      };
      setFetchProductParams(fetchParam);
      productApiClient
        .getAlbumList(fetchParam, formatType)
        .then((response) => {
          const result: IProductList[] = response.data.map((item: any) => {
            return {
              title: item.title,
              labelName: item.label.name,
              albumState: item.albumState,
              packaging: item.packaging?.name,
              displayArtist: item.displayArtist,
              format: item.format,
              gtin: item.gtin,
              id: item.id,
              releaseDate: item.releaseDate,
              catalogNumber: item.catalogNumber,
              priceCode: preferPrimaryPpdReferencePrice
                ? getPrimaryPpdReferencePriceOrLegacyPriceCode(item)
                : item.albumMetaData?.pricing?.priceCode,
              trackCount: item.tracks?.length,
              updatedAt: item.updatedAt,
              versions: item.albumMetaData?.versions,
              packagingDescription: item.albumMetaData?.packagingDescription,
              exclusiveReleaseAccountName:
                item.albumMetaData?.exclusiveReleaseAccountName,
              originalReleaseDate: item?.originalReleaseDate,
              preOrderDate: item.albumMetaData?.retailPreOrderDate,
              preOrderTime: item.albumMetaData?.retailPreOrderTime,
              releaseType: item.albumMetaData?.releaseType,
              videoContentType: item.albumMetaData?.videoContentType,
              merchandiseType: item.merchandiseMetadata?.merchandiseType,
              activeChangeRequestId: item.activeChangeRequestId,
            };
          });
          setResultCount(result.length);
          params.success({
            rowData: result,
            rowCount: response.totalRowCount,
          });
        })
        .catch((error) => {
          logger.error(error);
          params.fail();
        });
    },
    [
      searchOptions,
      filterOptions,
      albumStateFilter,
      formatFilter,
      merchandiseTypeFilter,
      formatType,
      preferPrimaryPpdReferencePrice,
    ]
  );

  const processAlbum = useCallback(
    async (data: any, options: string) => {
      if (options === Edit) {
        const productViewMode = getProductViewMode(
          data?.albumState as AlbumState,
          canUserEditProduct(data.albumState as AlbumState)
        );
        const productUrlMode =
          productViewMode === ProductViewModes.Edit
            ? 'EditProduct'
            : 'ProductDetails';

        formatType === FormatType.Merchandise
          ? navigate(`/Merchandise/${productUrlMode}/${data.id}`)
          : navigate(`/Physical/${productUrlMode}/Album/${data.id}`);
      } else if (options === Delete) {
        const header = `${t('product.productList.deleteDraft')} "${data.title}" ?`;
        setWarningHeader(header);
        setShowPopUp(true);
        setselectedId(data.id);
      } else if (options === Duplicate) {
        setAlbumToCopy(data);
        setShowCreateProductModal(true);
      } else if (options === DuplicateForDigital) {
        setAlbumToCopy(data);
        setShowDuplicateForDigitalModal(true);
      }
    },
    [canUserEditProduct, formatType, navigate, t]
  );

  const onDeleteProduct = useCallback(async () => {
    await productApiClient.deleteAlbum(selectedId.toString());
    setRefreshGrid(!refreshGrid);
    setShowPopUp(!showPopUp);
  }, [selectedId, refreshGrid, showPopUp]);

  const toggleCreateProductModal = () => {
    setShowCreateProductModal(!showCreateProductModal);
  };

  const toggleDuplicateForDigitalModal = () => {
    setShowDuplicateForDigitalModal(!showDuplicateForDigitalModal);
  };

  const openCreateProductModal = () => {
    setAlbumToCopy(undefined);
    setShowCreateProductModal(true);
  };

  return {
    t,
    refreshGrid,
    warningHeader,
    showPopUp,
    setShowPopUp,
    showCreateProductModal,
    toggleCreateProductModal,
    openCreateProductModal,
    showExportModal,
    setShowExportModal,
    albumToCopy,
    fetchProductParams,
    hasWriteAccess,
    productState,
    resultCount,
    initialFilters,
    initialSearch,
    finalColumndefs,
    getRows,
    onSortUpdated,
    updateFilters,
    processAlbum,
    onDeleteProduct,
    externalFiltersUpdated,
    setExternalFiltersUpdated,
    resetColumnFilters,
    showDuplicateForDigitalModal,
    toggleDuplicateForDigitalModal,
  };
};
